import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import {
  trainerDisplayName,
  weekdayLabel,
} from "../AdminOnboarding/scheduleStepUtils";
import { HubListGroup, HubRow } from "../AdminPortal/HubPwa";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import { Button, EmptyState, useToast } from "../../ui";

function slotTimeLabel(slot) {
  const start = String(slot.start_time || "").slice(0, 5);
  const end = String(slot.end_time || "").slice(0, 5);
  return `${weekdayLabel(Number(slot.weekday))}, ${start}–${end}`;
}

function formatConflictLine(conflict) {
  const day = weekdayLabel(Number(conflict.weekday));
  const start = String(conflict.start_time || "").slice(0, 5);
  const end = String(conflict.end_time || "").slice(0, 5);
  return `${day} ${start}–${end}: ${conflict.other_trainer_name}`;
}

export default function TrainerGroupSlotAssignment({
  groupId,
  groupName,
  trainerId,
  trainerName = "Тренер",
  onSaved,
  isPwa = false,
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [slots, setSlots] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clinicAPI
      .getDoctors({ active: true })
      .then((r) => setTrainers(r.doctors || r.trainers || []))
      .catch(() => setTrainers([]));
  }, []);

  const load = useCallback(async () => {
    if (!groupId || !trainerId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getGroupScheduleSlots(groupId);
      const list = data.slots || [];
      setSlots(list);
      setSelectedIds(
        new Set(
          list
            .filter((sl) => sl.trainer_id && String(sl.trainer_id) === String(trainerId))
            .map((sl) => sl.id),
        ),
      );
    } catch {
      setSlots([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [groupId, trainerId]);

  useEffect(() => {
    load();
  }, [load]);

  const sortedSlots = useMemo(
    () =>
      [...slots].sort((a, b) => {
        const dayA = a.weekday === 0 ? 7 : a.weekday;
        const dayB = b.weekday === 0 ? 7 : b.weekday;
        if (dayA !== dayB) return dayA - dayB;
        return String(a.start_time).localeCompare(String(b.start_time));
      }),
    [slots],
  );

  const toggleSlot = (slot) => {
    const ownerId = slot.trainer_id ? String(slot.trainer_id) : null;
    const isMine = ownerId === String(trainerId);
    const isFree = !ownerId;
    const isSelected = selectedIds.has(slot.id);

    if (!isFree && !isMine && !isSelected) {
      toast.error(
        `Слот занят: ${trainerDisplayName(slot.trainer_id, trainers) || "другой тренер"}`,
      );
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(slot.id)) next.delete(slot.id);
      else next.add(slot.id);
      return next;
    });
  };

  const save = async (force = false) => {
    if (!groupId || !trainerId) return;
    setSaving(true);
    try {
      const res = await clinicAPI.assignTrainerGroupSchedule(groupId, {
        trainer_id: trainerId,
        slot_ids: [...selectedIds],
        force,
      });
      toast.success(
        res.sessions_created || res.sessions_updated
          ? `Назначение сохранено · ${res.sessions_created || 0} новых и ${res.sessions_updated || 0} обновлённых занятий`
          : "Назначение тренера сохранено",
      );
      onSaved?.();
      await load();
    } catch (err) {
      const conflicts = err.response?.data?.conflicts;
      if (err.response?.status === 409 && conflicts?.length) {
        const detail = conflicts.map(formatConflictLine).join("\n");
        const ok = window.confirm(
          "Некоторые слоты уже назначены другим тренерам:\n\n" +
            `${detail}\n\n` +
            "Заменить тренера на этих слотах?",
        );
        if (ok) {
          setSaving(false);
          return save(true);
        }
        return;
      }
      toast.error(err.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className={s.groupsHubScheduleHint}>Загрузка слотов группы…</p>;
  }

  if (!sortedSlots.length) {
    return (
      <EmptyState
        title="Нет расписания группы"
        description={`Сначала настройте дни и время занятий для «${groupName}» в разделе «Группы». Здесь вы сможете назначить тренера на готовые слоты.`}
        actionLabel="Открыть группы"
        onAction={() => navigate("/manage/app/groups")}
      />
    );
  }

  const slotRow = (slot) => {
    const selected = selectedIds.has(slot.id);
    const ownerId = slot.trainer_id ? String(slot.trainer_id) : null;
    const isMine = ownerId === String(trainerId);
    const isOther = ownerId && !isMine;
    const ownerName = isOther
      ? trainerDisplayName(slot.trainer_id, trainers)
      : null;

    let meta = "Свободный слот";
    if (selected) meta = `${trainerName} ведёт это занятие`;
    else if (isMine) meta = `Сейчас назначен: ${trainerName}`;
    else if (isOther) meta = `Ведёт: ${ownerName}`;

    let badge;
    let badgeTone;
    if (selected) {
      badge = "Назначено";
      badgeTone = "ok";
    } else if (isOther) {
      badge = "Занят";
      badgeTone = "danger";
    }

    return (
      <HubRow
        key={slot.id}
        title={slotTimeLabel(slot)}
        meta={meta}
        badge={badge}
        badgeTone={badgeTone}
        onClick={() => toggleSlot(slot)}
      />
    );
  };

  return (
    <div className={s.trainerSlotAssignment}>
      <p className={s.groupsHubScheduleHint}>
        Здесь админ назначает, какие занятия группы «{groupName}» ведёт{" "}
        {trainerName}. Дни и время задаются в разделе «Группы» — тренер сам себе
        расписание не настраивает.
      </p>

      {isPwa ? (
        <HubListGroup>{sortedSlots.map(slotRow)}</HubListGroup>
      ) : (
        <ul className={s.trainerSlotAssignmentList}>
          {sortedSlots.map((slot) => {
            const selected = selectedIds.has(slot.id);
            const ownerId = slot.trainer_id ? String(slot.trainer_id) : null;
            const isOther = ownerId && ownerId !== String(trainerId);
            return (
              <li key={slot.id}>
                <button
                  type="button"
                  className={[
                    s.trainerSlotAssignmentCard,
                    selected ? s.trainerSlotAssignmentCardOn : "",
                    isOther && !selected ? s.trainerSlotAssignmentCardBusy : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => toggleSlot(slot)}
                >
                  <span
                    className={[
                      s.trainerSlotAssignmentCheck,
                      selected ? s.trainerSlotAssignmentCheckOn : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-hidden
                  >
                    {selected ? "✓" : ""}
                  </span>
                  <span className={s.trainerSlotAssignmentCardBody}>
                    <strong>{slotTimeLabel(slot)}</strong>
                    <span>
                      {selected
                        ? `Ведёт: ${trainerName}`
                        : isOther
                          ? `Ведёт: ${trainerDisplayName(slot.trainer_id, trainers)}`
                          : "Свободно"}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!isPwa ? (
        <p className={s.trainerSlotAssignmentFoot}>
          Дни и время группы редактируются в{" "}
          <Link to="/manage/app/groups">разделе «Группы»</Link>.
        </p>
      ) : null}

      <Button
        type="button"
        onClick={() => save(false)}
        disabled={saving}
        block={isPwa}
        className={isPwa ? aa.hubPrimaryCta : undefined}
      >
        {saving ? "Сохранение…" : "Сохранить назначение"}
      </Button>
    </div>
  );
}
